import { useId, useState, ChangeEvent, KeyboardEvent } from "react";
import { ITagButtonProps, TagButton } from "src/components-mui/common/tag/tag-button";
import { KEY_ENTER, KEY_SPACE } from "src/consts/keyboard";

const FILTER_TAG_CHECKBOX_TEST_ID = "filter-tag-checkbox";

type FilterTagCheckboxProps = {
    label: string;
    checked?: boolean;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    color?: ITagButtonProps["color"];
};

/**
 * FilterTagCheckbox
 * Accessible, button-like checkbox for tag filtering.
 * - Keyboard accessible (Space/Enter toggles)
 * - ARIA roles and states for screen readers
 * - Controlled/uncontrolled support
 * - Visually hidden native input for form compatibility
 */
export const FilterTagCheckbox = ({ label, checked, onChange, color = "gray" }: FilterTagCheckboxProps) => {
    const [internalChecked, setInternalChecked] = useState(false);
    const isControlled = checked !== undefined;
    const isChecked = isControlled ? checked : internalChecked;
    const checkboxId = useId();
    const isGrayColor = color === "gray";

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (!isControlled) setInternalChecked(event.target.checked);
        onChange?.(event);
    };

    const handleInternalKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
        if (event.key === KEY_SPACE || event.key === KEY_ENTER) {
            event.preventDefault();
            const syntheticEvent = {
                ...event,
                target: { checked: !isChecked },
            } as unknown as ChangeEvent<HTMLInputElement>;
            handleChange(syntheticEvent);
        }
    };

    return (
        <TagButton
            data-test-id={FILTER_TAG_CHECKBOX_TEST_ID}
            component="label"
            role="checkbox"
            aria-checked={isChecked}
            tabIndex={0}
            onKeyDown={handleInternalKeyDown}
            color={color}
            variant="outlined"
            size="extraSmall"
            sx={theme => ({
                "&:focus": { bgcolor: { xs: "transparent" } },
                ...(isChecked && {
                    bgcolor: { xs: isGrayColor ? `${color}.100` : `${color}.200` },
                    "&:focus": { bgcolor: { xs: `${color}.200` } },
                    "@media (hover: hover)": {
                        [theme.breakpoints.down("sm")]: {
                            "&:hover": { bgcolor: isGrayColor ? `${color}.100` : `${color}.200` },
                        },
                    },
                }),
            })}
        >
            <input id={checkboxId} type="checkbox" hidden checked={isChecked} onChange={handleChange} aria-hidden="true" tabIndex={-1} />
            <span>{label}</span>
        </TagButton>
    );
};
